<script setup lang="ts">
import type { Maker } from "~/types";
import { attributeTagVariant, compareAttributeTags } from "~/utils/attributeTagVariant";
import { isNewMaker } from "~/utils/isNewMaker";

interface Props {
  maker: Maker;
  showDate?: boolean;
  isLazy?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showDate: true,
  isLazy: true,
});

const route = useRoute();
const { attachmentUrl, staticUrl } = useImageUrl();

function hasLogoImage(maker: Maker): boolean {
  const logo = maker.attachments?.logo;
  return !!(logo && Array.isArray(logo) && logo.length > 0);
}

const makerIsNew = computed(() => isNewMaker(props.maker.createdAt));

const attributeTags = computed(() =>
  (props.maker.tags ?? []).filter((tag) => tag.category === "attribute").sort(compareAttributeTags),
);

function onImageError(e: Event) {
  const img = e.target as HTMLImageElement;
  img.src = staticUrl("/Images/site-images/placeholder.png");
}

const storeLink = computed(() => {
  const discount = route.query.discount as string | undefined;
  return {
    path: `/store/${props.maker.slug}/`,
    ...(discount ? { query: { discount } } : {}),
  };
});
</script>

<template>
  <NuxtLink
    class="maker-list"
    :to="storeLink"
  >
    <div class="maker-list__list">
      <div class="maker-list__list--image-wrapper">
        <img
          class="logo"
          :class="maker.is_active === false && 'opacity'"
          :loading="isLazy ? 'lazy' : 'eager'"
          :fetchpriority="isLazy ? 'auto' : 'high'"
          decoding="async"
          width="400"
          height="280"
          :src="
            hasLogoImage(maker)
              ? attachmentUrl(maker.attachments!.logo, 'medium')
              : staticUrl('/Images/site-images/placeholder.png')
          "
          :alt="`${maker.name} logo`"
          @error="onImageError"
        />
      </div>
      <h2
        v-if="maker.is_active === false"
        class="maker-list__list--vacation-text text-shadow white-text"
      >
        Back soon 🧳
      </h2>

      <div class="maker-list__list--content">
        <h4 class="maker-list__list--title">
          {{ maker.name }}
        </h4>

        <div
          v-if="maker.cuisines && maker.cuisines.length"
          class="maker-list__list--cuisines"
        >
          <Badge
            v-for="cuisine in maker.cuisines.slice(0, 3)"
            :key="cuisine.name"
            variant="default"
            size="sm"
          >
            {{ cuisine.name }}
          </Badge>
        </div>

        <div
          v-if="makerIsNew || attributeTags.length"
          class="maker-list__list--attributes"
        >
          <Pill
            v-if="makerIsNew"
            class="maker-list__list--new"
            text="NEW"
            color="dark"
            type="filled"
            size="small"
          />
          <Badge
            v-for="tag in attributeTags.slice(0, 2)"
            :key="tag.id"
            :variant="attributeTagVariant(tag)"
            size="lg"
          >
            {{ tag.name }}
          </Badge>
          <Badge
            v-if="attributeTags.length > 2"
            variant="default"
            size="lg"
          >
            +{{ attributeTags.length - 2 }}
          </Badge>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>

<style lang="scss" scoped>
.maker-list {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 95%;
  // Stretch within an items-stretch parent so every card in a row matches
  // the tallest card. A row with mixed content (some 2 pills, some 3 wrapped
  // pills) reads cleanly as a uniform grid instead of a ragged staircase.
  align-self: stretch;
  margin: 0.1rem 0 var(--space-3) 0.1rem;
  cursor: pointer;
  border-radius: var(--radius);
  text-decoration: none;
  color: inherit;

  // Hover: keep the warm border, lift via shadow + translate only.
  &:hover {
    .maker-list__list {
      box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.15);
      transform: translateY(-0.125rem);
      .logo {
        transform: scale(1.04);
      }
    }
  }

  &__list {
    position: relative;
    background-color: var(--color-white-1);
    border: 0.0625rem solid var(--color-grey-3);
    border-radius: var(--radius);
    box-shadow: var(--shadow-1);
    padding: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition:
      box-shadow var(--dur-base) var(--ease-out),
      transform var(--dur-base) var(--ease-out);

    a {
      text-decoration: none;
    }
    h2 {
      margin-bottom: 0 !important;
    }
    p {
      font-weight: var(--weight-regular) !important;
    }

    // Tighter content padding so the image dominates as the bulk of the
    // tile. Bottom 0.75rem keeps the rating snug against the card edge.
    &--content {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      padding: 0.75rem 1.25rem;
    }

    &--title {
      font-size: 0.9rem;
      line-height: 1.2;
      font-weight: var(--weight-semibold);
      letter-spacing: -0.01em;
      color: var(--color-grey-1) !important;
      margin: 0;
      // Single line + ellipsis so a long maker name doesn't push the
      // pills/rating row down and stagger sibling tiles.
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &--cuisines {
      display: flex;
      flex-wrap: wrap;
      column-gap: 0.5rem;
      row-gap: 0.5rem;
      margin: 0;
      overflow: hidden;
    }

    &--attributes {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      column-gap: 0.5rem;
      row-gap: 0.5rem;
      margin: 0;
    }

    // Image is the dominant visual — sized to roughly two-thirds of the
    // tile via aspect-ratio 3/2 so it scales with the card width while
    // staying taller than the text content below.
    &--image-wrapper {
      width: 100%;
      aspect-ratio: 3 / 2;
      border-radius: 0;
      overflow: hidden;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;

      .delivery-date {
        background-color: var(--color-grey-1);
      }
      .same-day {
        background-color: var(--color-pink-1);
      }
      .delivery-date,
      .same-day {
        color: var(--color-white-1);
        border-radius: var(--radius-pill) !important;
        left: 0.75rem;
        top: 0.75rem;
        position: absolute;
        display: flex;
        padding: 0.25rem 0.7rem;
        align-items: center;
        font-size: var(--text-xs);
        font-weight: var(--weight-semibold);
        p {
          color: var(--color-white-1) !important;
          font-size: var(--text-xs) !important;
          line-height: 1 !important;
          margin: 0 !important;
        }
        @media (max-width: $tablet) {
          width: auto;
          justify-content: center;
        }
      }

      .logo {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        display: block;
        transition: transform 500ms var(--ease-out);
      }
    }

    &--vacation-text {
      text-align: center;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 80%;
      color: #fff;
      text-shadow: 0 0.0625rem 0.25rem rgba(0, 0, 0, 0.5);
      margin: 0;
    }
  }
}

.opacity {
  opacity: 0.5;
}
</style>
